import { useTranslation } from "react-i18next"

import { Progress } from "@/components/ui/progress"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { cn } from "@/lib/utils"
import type { RewardTeaser } from "@/types/xp"

/**
 * OKS-765 — Progress toward the target reward: how close the creator's
 * spendable balance is to the reward's cost. Single source of truth for the
 * teaser percent, shared by the Profile §11 wallet card (OKS-513) and the
 * Missions-home reward teaser so the two surfaces can NEVER diverge.
 *
 * `target = balance + coins_to_go` is the reward's coin cost by construction
 * (`coins_to_go = coin_cost - balance`). Clamped to [0, 100]; a 0 target
 * (defensive — never happens since `coins_to_go >= 1`) reads as 0.
 *
 * @param coinBalance the creator's current spendable coin balance
 * @param rewardTeaser the nearest not-yet-affordable reward
 * @returns the progress percentage in [0, 100]
 */
export function rewardTeaserPercent(
  coinBalance: number,
  rewardTeaser: RewardTeaser,
): number {
  const target = coinBalance + rewardTeaser.coins_to_go
  if (target <= 0) return 0
  return Math.min(100, (coinBalance / target) * 100)
}

interface RewardTeaserBarProps {
  /** The creator's current spendable coin balance. */
  coinBalance: number
  /** The nearest not-yet-affordable reward (non-null — the caller hides on null). */
  rewardTeaser: RewardTeaser
  /** Optional extra classes for the wrapper (layout tuning per host). */
  className?: string
  /** Optional testid on the wrapper — Profile passes `coins-wallet-teaser`. */
  "data-testid"?: string
  /**
   * Clamp the teaser line to a single line with ellipsis (Missions home, UX
   * Spec §5 — long titles must not wrap/shift). Profile leaves it unset so the
   * line wraps as before.
   */
  truncate?: boolean
}

/**
 * OKS-765 — Shared presentational teaser: a shadcn `Progress` bar + the
 * "{value} coins from the {reward}" line (`profile.wallet.teaser`). Extracted
 * from `coins-wallet-card.tsx` (OKS-513) so Profile and the Missions home
 * render identical markup + percent math (PRD OQ-5 shared-component path).
 *
 * Purely presentational — the caller decides whether there is anything to
 * tease (renders nothing when `rewardTeaser` is null). Profile wraps this in
 * its existing `data-testid="coins-wallet-teaser"` container (passed through
 * here) so its shipped specs stay green; the Missions card wraps it in the
 * gold reward card.
 */
export function RewardTeaserBar({
  coinBalance,
  rewardTeaser,
  className,
  "data-testid": dataTestId,
  truncate = false,
}: RewardTeaserBarProps) {
  const { t } = useTranslation()
  const percent = rewardTeaserPercent(coinBalance, rewardTeaser)

  return (
    <div
      data-testid={dataTestId}
      className={cn("flex flex-col gap-1.5", className)}
    >
      <Progress value={percent} />
      <span
        className={cn("text-muted-foreground text-xs", truncate && "truncate")}
      >
        {t("profile.wallet.teaser", {
          value: formatCompactNumber(rewardTeaser.coins_to_go),
          reward: rewardTeaser.reward_title,
        })}
      </span>
    </div>
  )
}
